import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Plus, Settings, LogOut, Building2, MessageSquare, X, TrendingUp, Handshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { eur, pct, deltaPct, STATUS_LABEL } from "@/format";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput, StatusPill } from "@/pages/admin/officeUi";
import MessageThread from "@/components/MessageThread";

export default function AdminHome() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [structures, setStructures] = useState([]);
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, sum, msg] = await Promise.all([api.listStructures(), api.summary(), api.adminMessages()]);
      setStructures(s); setSummary(sum); setMessages(msg);
    } catch (e) {
      toast.error(e.message || "Errore di caricamento");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unread = messages.filter((m) => m.status === "unread").length;
  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  const markStatus = async (mid, status) => {
    try { await api.setMessageStatus(mid, status); setMessages(await api.adminMessages()); }
    catch (e) { toast.error(e.message); }
  };

  const replyMessage = async (mid, text) => {
    try {
      const res = await api.adminReplyMessage(mid, text);
      setMessages(await api.adminMessages());
      toast.success(res.emails_sent > 0 ? "Risposta inviata · cliente avvisato via email" : "Risposta inviata");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="theme-office min-h-screen" style={{ background: OFFICE.surface2, color: OFFICE.ink }}>
      <header className="sticky top-0 z-30" style={{ background: OFFICE.surface, borderBottom: `1px solid ${OFFICE.border}` }}>
        <div className="max-w-6xl mx-auto px-5 md:px-6 h-16 flex items-center justify-between">
          <div className="font-display text-lg">YU <span style={{ color: OFFICE.brand }}>YieldUp</span> <span className="hidden sm:inline text-sm font-sans font-normal" style={{ color: OFFICE.muted }}>· Pannello team</span></div>
          <div className="flex items-center gap-2">
            <OfficeButton variant="ghost" className="!px-3 sm:!px-5" onClick={() => navigate("/admin/partner")} data-testid="admin-partners-nav"><Handshake className="h-4 w-4" /> <span className="hidden sm:inline">Partner</span></OfficeButton>
            <OfficeButton variant="ghost" className="!px-3 sm:!px-5" onClick={() => navigate("/admin/settings")} aria-label="Impostazioni"><Settings className="h-4 w-4" /> <span className="hidden sm:inline">Impostazioni</span></OfficeButton>
            <OfficeButton variant="ghost" className="!px-3 sm:!px-5" onClick={doLogout} aria-label="Esci"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Esci</span></OfficeButton>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl">Il tuo portafoglio</h1>
            <p className="mt-1" style={{ color: OFFICE.muted }}>Gestisci strutture, dati periodici e accessi dei clienti.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <OfficeButton variant="ghost" onClick={() => navigate("/admin/partner?tab=partners")} data-testid="admin-add-partner-btn"><Handshake className="h-4 w-4" /> Partner e segnalazioni</OfficeButton>
            <OfficeButton onClick={() => setShowNew(true)}><Plus className="h-4 w-4" /> Nuova struttura</OfficeButton>
          </div>
        </div>

        {/* Portfolio summary */}
        {summary ? (
          <OfficeCard className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="Strutture" value={summary.structures_total} sub={`${summary.operative} operative · ${summary.collaudo} in collaudo`} />
              <Stat label={`Fatturato ${summary.ytd_year} (YTD)`} value={eur(summary.ytd_revenue)} />
              {summary.latest ? (
                <Stat label={`Fatturato ${summary.latest.period_label}`} value={eur(summary.latest.revenue)}
                  delta={summary.yoy ? deltaPct(summary.latest.revenue, summary.yoy.revenue) : null} />
              ) : <Stat label="Ultimo mese" value="—" />}
              {summary.latest ? (
                <Stat label="Occupazione media" value={pct(summary.latest.occupancy)} />
              ) : <Stat label="Occupazione media" value="—" />}
            </div>
          </OfficeCard>
        ) : null}

        {/* Structures list */}
        <h2 className="mt-10 font-display text-xl">Strutture</h2>
        <div className="mt-4 space-y-3">
          {loading ? <p style={{ color: OFFICE.muted }}>Caricamento…</p> :
            structures.length === 0 ? (
              <OfficeCard><p style={{ color: OFFICE.muted }}>Nessuna struttura. Creane una con "Nuova struttura".</p></OfficeCard>
            ) : structures.map((s) => (
              <div key={s.id} className="rounded-2xl border p-5 flex items-center justify-between gap-4"
                style={{ background: OFFICE.surface, borderColor: OFFICE.border }}>
                <button onClick={() => navigate(`/admin/structures/${s.id}`)} className="flex items-center gap-4 text-left flex-1">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: OFFICE.brandTertiary, color: OFFICE.brand }}>
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: OFFICE.ink }}>{s.name}</div>
                    <div className="text-sm" style={{ color: OFFICE.muted }}>
                      {s.location || "—"} · {s.periods_count || 0} mesi · {s.clients?.length || 0} accessi
                      {s.last_period_label ? ` · ultimo: ${s.last_period_label}` : ""}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <StatusPill status={s.status} />
                  <button title="Vedi come il cliente" onClick={() => navigate(`/admin/preview/${s.id}`)}
                    className="h-9 w-9 rounded-full flex items-center justify-center transition-colors hover:bg-[#FDEEE7]"
                    style={{ border: `1px solid ${OFFICE.border}`, color: OFFICE.brand }}>
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
        </div>

        {/* Segnalazioni */}
        <div className="mt-10 flex items-center gap-3">
          <h2 className="font-display text-xl">Segnalazioni</h2>
          {unread > 0 ? <span className="rounded-full px-2.5 py-0.5 text-xs font-medium text-white" style={{ background: OFFICE.brand }}>{unread} non lette</span> : null}
        </div>
        <div className="mt-4 space-y-3">
          {messages.length === 0 ? (
            <OfficeCard><p style={{ color: OFFICE.muted }}>Nessuna segnalazione ricevuta.</p></OfficeCard>
          ) : messages.map((m) => (
            <OfficeCard key={m.id}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2 text-sm" style={{ color: OFFICE.muted }}>
                  <MessageSquare className="h-4 w-4" style={{ color: OFFICE.brand }} />
                  <span className="font-medium" style={{ color: OFFICE.ink }}>{m.structure_name}</span>
                  · {m.client_email}
                </div>
                {m.status !== "read" && m.status !== "replied" ? (
                  <OfficeButton variant="ghost" className="!py-1.5 !px-3 shrink-0" onClick={() => markStatus(m.id, "read")}>Segna letta</OfficeButton>
                ) : null}
              </div>
              <MessageThread
                message={m}
                mineSender="admin"
                theme="light"
                onReply={(text) => replyMessage(m.id, text)}
              />
            </OfficeCard>
          ))}
        </div>
      </main>

      {showNew ? <NewStructureModal onClose={() => setShowNew(false)} onCreated={(s) => { setShowNew(false); navigate(`/admin/structures/${s.id}`); }} /> : null}
    </div>
  );
}

function Stat({ label, value, sub, delta }) {
  return (
    <div>
      <div className="text-sm" style={{ color: OFFICE.muted }}>{label}</div>
      <div className="mt-1 font-display text-2xl" style={{ color: OFFICE.ink }}>{value}</div>
      {sub ? <div className="text-xs mt-1" style={{ color: OFFICE.muted }}>{sub}</div> : null}
      {delta && delta.dir !== "none" ? (
        <div className="text-xs mt-1 font-mono" style={{ color: delta.dir === "up" ? "#2E8B4F" : "#C0341A" }}>{delta.text} vs anno prec.</div>
      ) : null}
    </div>
  );
}

function NewStructureModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [status, setStatus] = useState("COLLAUDO");
  const [strategy, setStrategy] = useState("IN_ATTIVAZIONE");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Inserisci un nome"); return; }
    setBusy(true);
    try {
      const s = await api.createStructure({ name: name.trim(), location: location.trim(), status, pricing_strategy: strategy });
      toast.success("Struttura creata");
      onCreated(s);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(20,18,16,0.45)" }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: OFFICE.surface }}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl" style={{ color: OFFICE.ink }}>Nuova struttura</h3>
          <button onClick={onClose}><X className="h-5 w-5" style={{ color: OFFICE.muted }} /></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <OfficeField label="Nome"><OfficeInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Villa Aurora" /></OfficeField>
          <OfficeField label="Località"><OfficeInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Taormina" /></OfficeField>
          <div className="grid grid-cols-2 gap-4">
            <OfficeField label="Stato">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-xl border px-4 py-2.5 outline-none" style={{ borderColor: OFFICE.border, background: OFFICE.surface, color: OFFICE.ink }}>
                <option value="COLLAUDO">In collaudo</option>
                <option value="OPERATIVA">Operativa</option>
              </select>
            </OfficeField>
            <OfficeField label="Strategia prezzi">
              <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className="w-full rounded-xl border px-4 py-2.5 outline-none" style={{ borderColor: OFFICE.border, background: OFFICE.surface, color: OFFICE.ink }}>
                <option value="IN_ATTIVAZIONE">In attivazione</option>
                <option value="ATTIVA">Attiva</option>
              </select>
            </OfficeField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <OfficeButton type="button" variant="ghost" onClick={onClose}>Annulla</OfficeButton>
            <OfficeButton type="submit" disabled={busy}>{busy ? "Creazione…" : "Crea struttura"}</OfficeButton>
          </div>
        </form>
      </div>
    </div>
  );
}
